import { Box, Button, Flex, Grid, HStack, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Skeleton, SkeletonText, Tab, TabList, Tabs, Text, useDisclosure, VStack } from "@chakra-ui/react"
import React, { useEffect, useRef, useState } from "react"
import { EnergyConsumptionReport } from "../../api/types"
import CarbonIntensityIcon from "../../../../assets/icons/CarbonIntensity"
import CloudIcon from "../../../../assets/icons/CloudIcon"
import { ConsumedEnergyIcon } from "../../../../assets/icons/ConsumedEnergy"
import CostForConsumptionIcon from "../../../../assets/icons/CostConsumptionIcon"
import GeneratedIncomeIcon from "../../../../assets/icons/GeneretedIncomeIcon"
import ProducedEnergyIcon from "../../../../assets/icons/ProducedEnergyIcon"
import { KILOWATT_HOUR, CARBON_FOOTPRINT_KG, MONETARY_VALUE, CARBON_INTENSITY } from "../../../../utils/consts"
import { Card } from "./EnergyReportComponents/CardComponent"
import { getShadeFromPercent } from "../../../../utils/colors"
import SendEmail from "./SendEmail"
import RecurringReports from "./RecurringReports"
import BookmarkReport from "./BookmarkReport"
import { EnergyConsumptionModal } from "./EnergyReportComponents/EnergyConsumptionModal"
import { scrollBarStyle } from "../../../../utils/helpers"
import html2canvas from 'html2canvas'
import { Thumbnail } from "./EnergyReportComponents/EnergyConsumptionThumbnail"
import EnergyByDeviceCard from "./EnergyReportComponents/EnergyByDeviceCard"
import ExportCsv from "./ExportCsv"

type Props = {
  data: EnergyConsumptionReport
  startDate: Date
  endDate: Date
  reportName: string
  isDownloading: boolean
  isLoading: boolean
  formData: any
  handleDownload: () => void
  handleGenerate: () => void
}

export function getLocationsPercentages(
  currentValue: number,
  previousValue: number,
) {
  if (currentValue > 0 && previousValue > 0) {
    const percentageChange = ((previousValue - currentValue) / previousValue) * 100
    return Number(percentageChange.toFixed(2))
  }
  return 0
}

function getBase64FileSizeInKB(base64: string): number {
  const base64Data = base64.split(',')[1]
  if (!base64Data) return 0

  const padding = (base64Data.match(/=+$/) || [''])[0].length
  const sizeInBytes = (base64Data.length * 3) / 4 - padding

  return sizeInBytes / 1024
}

const EnergyCardSkeleton = () => (
  <Box width="100%" borderRadius="xl" p={4} bg="white" boxShadow="sm">
    <Flex justify="space-between" align="center" mb={3}>
      <Skeleton height="20px" width="50%" borderRadius="md" />
      <Skeleton boxSize="32px" borderRadius="full" />
    </Flex>
    <Skeleton height="32px" width="70%" borderRadius="md" />
    <SkeletonText mt={4} noOfLines={2} spacing="3" skeletonHeight="4" />
  </Box>
)

export const EnergyConsumptionReportPdf = ({ data, formData, startDate, endDate, reportName, isDownloading, isLoading, handleDownload, handleGenerate }: Props) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);
  const hiddenRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let tries = 0;
    const maxTries = 10;
    const interval = 100;

    const waitForRender = setInterval(() => {
      const messageBoxes = document.querySelectorAll('[data-location]');
      const arrowIcons = document.querySelectorAll('.arrowIconThumbnailClass');

      if (messageBoxes.length > 0 || tries >= maxTries) {
        clearInterval(waitForRender);

        messageBoxes.forEach((box) => {
          (box as HTMLElement).style.transform = 'translate(0%, -30%)';
        });
        arrowIcons.forEach((icon) => {
          (icon as HTMLElement).style.transform = 'translate(0%, 12%)';
        });

        if (hiddenRef.current) {
          html2canvas(hiddenRef.current, {
            useCORS: true,
            scale: 10,
          }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            setThumbnail(imgData);

            const sizeKB = getBase64FileSizeInKB(imgData);
            setFileSize(sizeKB.toFixed(2));
          });
        }
      }

      tries++;
    }, interval);

    return () => clearInterval(waitForRender);
  }, [startDate, endDate, data]);

  const isDataEmpty = !data || data.length === 0
  const loading = isLoading || isDataEmpty

  const totalStats = !loading
    ? data.reduce(
      (acc, loc) => {
        acc.currConsumption += loc.value
        acc.prevConsumption += loc.previous_value
        acc.currGeneration += loc.generated_value
        acc.prevGeneration += loc.generated_previous_value
        acc.currFootprint += loc.carbon_footprint
        acc.prevFootprint += loc.previous_carbon_footprint
        acc.currCost += loc.total_cost
        acc.prevCost += loc.previous_cost
        acc.currIncome += loc.generated_total_cost
        acc.prevIncome += loc.generated_previous_cost
        acc.intensity += loc.carbon_intensity
        return acc
      },
      {
        currConsumption: 0,
        prevConsumption: 0,
        currGeneration: 0,
        prevGeneration: 0,
        currFootprint: 0,
        prevFootprint: 0,
        currCost: 0,
        prevCost: 0,
        currIncome: 0,
        prevIncome: 0,
        intensity: 0,
      }
    )
    : null

  const hasAllTab = !loading && data.length > 1
  const isAllTab = hasAllTab && selectedTab === 0
  const locationIndex = hasAllTab ? selectedTab - 1 : selectedTab
  const locationData = !loading && !isAllTab ? data[locationIndex] : null

  const displayData = !loading
    ? isAllTab
      ? {
        currConsumption: totalStats!.currConsumption,
        prevConsumption: totalStats!.prevConsumption,
        currGeneration: totalStats!.currGeneration,
        prevGeneration: totalStats!.prevGeneration,
        currFootprint: totalStats!.currFootprint,
        prevFootprint: totalStats!.prevFootprint,
        currCost: totalStats!.currCost,
        prevCost: totalStats!.prevCost,
        currIncome: totalStats!.currIncome,
        prevIncome: totalStats!.prevIncome,
        intensity: totalStats!.intensity,
      }
      : {
        currConsumption: locationData!.value,
        prevConsumption: locationData!.previous_value,
        currGeneration: locationData!.generated_value,
        prevGeneration: locationData!.generated_previous_value,
        currFootprint: locationData!.carbon_footprint,
        prevFootprint: locationData!.previous_carbon_footprint,
        currCost: locationData!.total_cost,
        prevCost: locationData!.previous_cost,
        currIncome: locationData!.generated_total_cost,
        prevIncome: locationData!.generated_previous_cost,
        intensity: locationData!.carbon_intensity,
      }
    : {
      currConsumption: 0,
      prevConsumption: 0,
      currGeneration: 0,
      prevGeneration: 0,
      currFootprint: 0,
      prevFootprint: 0,
      currCost: 0,
      prevCost: 0,
      currIncome: 0,
      prevIncome: 0,
      intensity: 0,
    }

  const locationsWithStats = !loading
    ? data
      .map((loc) => {
        const baseColor = "#157fee"
        const percent = (loc.value / displayData.currConsumption) * 100
        const color = getShadeFromPercent(baseColor, percent)
        return { ...loc, percent, color }
      })
      .sort((a, b) => b.percent - a.percent)
    : []

  const renderModalContent = (ref?: React.Ref<HTMLDivElement>) => (
    <ModalContent
      ref={ref}
      borderRadius="24px"
      maxW={840}
      height={800}
      maxH={800}
      overflow="hidden auto"
      sx={scrollBarStyle}
    >
      <ModalHeader position="sticky" top={0} zIndex={1} bg="white">
        Preview
      </ModalHeader>
      <ModalBody>
        <EnergyConsumptionModal
          data={data}
          displayData={displayData}
          startDate={startDate}
          endDate={endDate}
          isDownloading={false}
        />
      </ModalBody>
      <ModalFooter position="sticky" bottom={0} zIndex={1} bg="white" gap={3}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button colorScheme="blue" isLoading={isDownloading} onClick={handleDownload}>Download</Button>
      </ModalFooter>
    </ModalContent>
  )

  const renderThumbnail = () => (
    <Box
      ref={hiddenRef}
      position="absolute"
      top="-9999px"
      left="-9999px"
      width="794px"
      height="1123px"
    >
      <Thumbnail
        data={data}
        displayData={displayData}
        startDate={startDate}
        endDate={endDate}
      />
    </Box>
  )

  const isGroupedByLocation = data?.[0]?.location_id !== undefined

  return (
    <>
      <Flex direction={{ base: 'column', desktopLg: 'row' }} gap={8}>
        <Box flex="1">
          {loading ? (
            <Skeleton height="40px" width="300px" borderRadius="20px" />
          ) : (
            <Tabs
              variant="soft-rounded"
              w="fit-content"
              bg="white"
              rounded="3xl"
              colorScheme="gray"
              padding="4px"
              onChange={(index) => setSelectedTab(index)}
            >
              <TabList>
                {data.length > 1 && <Tab px={7}>All</Tab>}
                {data.map((loc) => (
                  <Tab px={7} key={loc?.location_id || loc?.group_id}>{loc.title}</Tab>
                ))}
              </TabList>
            </Tabs>
          )}
        </Box>
      </Flex>

      <Box mt={4}>
        <Flex direction={{ base: 'column', desktopLg: 'row' }} gap={8}>
          <Box flex="1" fontSize={20} fontWeight={700}>
            <Flex direction="column" gap={1}>
              <Flex gap={3}>
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Total consumed energy"
                    measurementType={KILOWATT_HOUR}
                    value={displayData.currConsumption}
                    percentageChange={getLocationsPercentages(displayData.currConsumption, displayData.prevConsumption)}
                  >
                    <ConsumedEnergyIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Produced energy"
                    measurementType={KILOWATT_HOUR}
                    value={displayData.currGeneration}
                    percentageChange={getLocationsPercentages(displayData.currGeneration, displayData.prevGeneration)}
                  >
                    <ProducedEnergyIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Carbon footprint"
                    measurementType={CARBON_FOOTPRINT_KG}
                    value={displayData.currFootprint}
                    percentageChange={getLocationsPercentages(displayData.currFootprint, displayData.prevFootprint)}
                  >
                    <CloudIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
              </Flex>
              <Flex gap={3}>
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Cost for consumption"
                    measurementType={MONETARY_VALUE}
                    value={displayData.currCost}
                    percentageChange={getLocationsPercentages(displayData.currCost, displayData.prevCost)}
                  >
                    <CostForConsumptionIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Generated income"
                    measurementType={MONETARY_VALUE}
                    value={displayData.currIncome}
                    percentageChange={getLocationsPercentages(displayData.currIncome, displayData.prevIncome)}
                  >
                    <GeneratedIncomeIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
                {loading ? (
                  <EnergyCardSkeleton />
                ) : (
                  <Card
                    width="100%"
                    title="Carbon intensity"
                    measurementType={CARBON_INTENSITY}
                    value={displayData.intensity}
                  >
                    <CarbonIntensityIcon boxSize="32px" color="text.secondary" />
                  </Card>
                )}
              </Flex>
            </Flex>

            {loading ? (
              <Box
                rounded="xl"
                bg="white"
                px="20px"
                py="16px"
                boxShadow="sm"
                mt={5}
              >
                <Flex align="center" paddingLeft={3} mb={3}>
                  <Skeleton height="14px" width="80px" borderRadius="md" mr={3} />
                  <Skeleton height="56px" width="50%" borderRadius="md" mr={3} />
                  <Flex direction="column" alignItems="center" justifyContent="center">
                    <Skeleton height="18px" width="40px" borderRadius="md" mb={1} />
                    <Skeleton height="14px" width="80px" borderRadius="md" />
                  </Flex>
                </Flex>
              </Box>
            ) : isAllTab ? (
              <Box
                rounded="xl"
                bg="white"
                px="20px"
                py="16px"
                boxShadow="sm"
                mt={5}
              >
                <Text fontWeight={700} fontSize="14px" mb={3}>Energy Consumption by {isGroupedByLocation ? "Location" : "Group"}</Text>
                {locationsWithStats.map((loc) => (
                  <Grid
                    key={loc?.location_id || loc?.group_id}
                    templateColumns="110px 1fr 150px"
                    alignItems="center"
                    gap={4}
                    mb={3}
                    px={3}
                  >
                    {/* Title */}
                    <Text
                      fontWeight={700}
                      fontSize="14px"
                      color="#707070"
                      overflowWrap="break-word"
                      wordBreak="break-word"
                      whiteSpace="normal"
                    >
                      {loc.title}
                    </Text>

                    {/* Bar container with background */}
                    <Box
                      position="relative"
                      height="56px"
                      width="100%"
                      bg="#f0f0f0"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      {loc.percent > 0 && (
                        <Box
                          bg={loc.color}
                          height="100%"
                          width={`${loc.percent}%`}
                          transition="width 0.3s ease"
                        />
                      )}
                    </Box>

                    <Flex direction="column" alignItems="center" justifyContent="center">
                      <Text fontWeight="700" fontSize="14px">{Math.round(loc.percent)}%</Text>
                      <Text ml={2} color="gray.500" fontSize="12px" display="flex" gap={1}>
                        {loc.value.toLocaleString()}<span>kWh</span>
                      </Text>
                    </Flex>
                  </Grid>
                ))}
              </Box>
            ) : (
              <EnergyByDeviceCard locationData={locationData} selectedDevices={formData.selectedDevices}/>
            )}
          </Box>

          {loading ? (
            <Box
              flex={{ base: "1", desktopLg: "0 0 30%" }}
              bg="white"
              rounded="xl"
              px="6"
              py="6"
              boxShadow="sm"
              height="450"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="space-between"
            >
              <VStack spacing="4" align="center" w="100%">
                <Skeleton height="200px" width="150px" borderRadius="md" />
                <Skeleton height="20px" width="70%" />
                <Skeleton height="16px" width="40%" />
              </VStack>

              <VStack spacing="3" w="100%">
                <Skeleton height="40px" width="60%" borderRadius="md" />
                <HStack spacing="4" justify="center">
                  <Skeleton height="20px" width="20px" borderRadius="full" />
                  <Skeleton height="20px" width="20px" borderRadius="full" />
                  <Skeleton height="20px" width="20px" borderRadius="full" />
                </HStack>
              </VStack>
            </Box>
          ) : (
            <Box
              flex={{ base: "1", desktopLg: "0 0 30%" }}
              bg="white"
              rounded="xl"
              px="60px"
              position="sticky"
              py="20px"
              boxShadow="sm"
              height="450px"
            >
              <Flex direction="column" alignItems="center" justifyContent="center" gap={2}>
                <Box onClick={onOpen} width={200} height={250} overflow="hidden" borderRadius="md" boxShadow="md" cursor="pointer">
                  {thumbnail ? (
                    <img src={thumbnail} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Flex align="center" justify="center" height="100%" bg="gray.100">
                      Loading...
                    </Flex>
                  )}
                </Box>

                <Flex direction="column" alignItems="center" justifyContent="center">
                  <Text fontWeight={700} fontSize="16px" color="#484848">Energy Consumption Report</Text>
                  <Text fontWeight={400} fontSize="13px" color="#9B9B9B">PDF • {fileSize ? `${fileSize} KB` : "Calculating..."}</Text>
                </Flex>

                <Button colorScheme="blue" onClick={handleDownload} isLoading={isDownloading}>
                  Download
                </Button>

                {renderThumbnail()}
                <Flex gap={3} alignItems="center">
                  <BookmarkReport
                    startDate={startDate}
                    endDate={endDate}
                    disabled={false}
                    locations={formData.selectedLocations}
                    deviceGroups={formData.selectedGroups}
                    reportType={formData.selectedReportType}
                    devices={formData.selectedDevices}
                    reportName={reportName}
                    filters_type="bookmark"
                    isDownloading={false}
                    savedAs="pdf"
                  />
                  <RecurringReports
                    startDate={startDate}
                    endDate={endDate}
                    disabled={false}
                    locations={formData.selectedLocations}
                    deviceGroups={formData.selectedGroups}
                    reportType={formData.selectedReportType}
                    devices={formData.selectedDevices}
                    reportName={reportName}
                    filters_type="recurring"
                    isDownloading={false}
                    savedAs={'pdf'}
                  />
                  <SendEmail
                    reportType="pdf"
                    reportName={reportName}
                    handleClick={handleGenerate}
                    disabled={false}
                    startDate={startDate}
                    endDate={endDate}
                  />
                  <ExportCsv
                    startDate={startDate}
                    endDate={endDate}
                    deviceIds={formData.selectedDevices}
                    reportName={reportName}
                    reportType={formData.selectedReportType}
                    groupIds={formData.selectedGroups}
                  />
                </Flex>
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
                  <ModalOverlay />
                  <ModalContent
                    borderRadius="24px"
                    maxW={840}
                    height={800}
                    maxH={800}
                    overflow={'hidden auto'}
                    sx={{ ...scrollBarStyle }}
                  >
                    {renderModalContent()}
                  </ModalContent>
                </Modal>
                <Modal isOpen={true} onClose={() => { }} isCentered={false}>
                  <Box
                    position="absolute"
                    top="-9999px"
                    left="-9999px"
                    visibility="hidden"
                    pointerEvents="none"
                    zIndex={-1}
                  >
                    {renderModalContent(modalRef)}
                  </Box>
                </Modal>
              </Flex>
            </Box>
          )}
        </Flex >
      </Box >
    </>
  )
}
